/* eslint-disable no-debugger */
import React from 'react';
import { browserHistory } from 'react-router';
import _ from 'lodash';
import validator from 'validator';
import { connect } from 'react-redux';
import common from './common';
import FormElement from '../../../shared/form/FormElement';
import * as campaignVersionActions from '../../../../actions/admin/campaignVersionActions';
import * as globalMessageActions from '../../../../actions/globalMessageActions';
import { Dialog } from 'primereact/components/dialog/Dialog';
import { Button } from 'primereact/components/button/Button';
import { Panel } from 'primereact/components/panel/Panel';
import { Card } from 'primereact/components/card/Card';

import RewardPoolDropbox from '../../../shared/dropbox/RewardPoolDropbox';
import campaignService from '../../../../utils/services/campaign';
import campaignVersionService from '../../../../utils/services/campaignVersion';

import './_campaignVersion.scss';

const SHOPIFY_SOURCE_TAG = 'STATIC_PAGE_ON_SORETO_SHOPIFY';

// Shown on the order status card when the campaign has no dedicated image.
// Same file the order status extension uses as its background fallback.
const DEFAULT_ORDER_STATUS_BANNER =
  'https://dist.soreto.com/cdn/shopify/campaign_themes/neutral/modal_background.png';

const SHOPIFY_PLACEMENTS = [
  {
    title: 'Referral page',
    images: [
      { key: 'logoUrl', label: 'Logo', aspect: '1 / 1' },
      { key: 'pageIcoUrl', label: 'Page icon (favicon)', aspect: '1 / 1' },
      { key: 'staticPageHeroUrl', label: 'Static page hero', aspect: '2 / 1' },
      { key: 'landingBackgroundUrl', label: 'Landing page background', aspect: '16 / 9' },
    ],
    fields: [],
  },
  {
    title: 'Floating widget',
    hint: 'The form asks for a name and email, then a Continue button. Those labels are set here.',
    images: [
      { key: 'floatingWidgetImageUrl', label: 'Floating widget image', aspect: '2 / 1' },
    ],
    fields: [
      { key: 'header', label: 'Title' },
      { key: 'description', label: 'Body', textArea: true },
      { key: 'ctaText', label: 'CTA text' },
      { key: 'detailsText', label: 'Details text', placeholder: 'Enter your details to get your personal share link.', textArea: true },
      { key: 'firstNameLabel', label: 'First name label', placeholder: 'First name' },
      { key: 'emailLabel', label: 'Email label', placeholder: 'Email' },
      { key: 'continueButtonText', label: 'Continue button', placeholder: 'Continue' },
      { key: 'primaryColor', label: 'Primary colour', placeholder: '#111111', color: true },
      { key: 'secondaryColor', label: 'Secondary colour', placeholder: '#ffffff', color: true },
    ],
  },
  {
    title: 'Inline widget',
    hint: 'Title, body, and form labels for the inline card only. Button colours are set on the floating widget.',
    images: [],
    fields: [
      { key: 'inlineHeader', label: 'Title' },
      { key: 'inlineDescription', label: 'Body', textArea: true },
      { key: 'inlineDetailsText', label: 'Details text', placeholder: 'Enter your details to get your personal share link.', textArea: true },
      { key: 'inlineFirstNameLabel', label: 'First name label', placeholder: 'First name' },
      { key: 'inlineEmailLabel', label: 'Email label', placeholder: 'Email' },
      { key: 'inlineContinueButtonText', label: 'Continue button', placeholder: 'Continue' },
    ],
  },
  {
    title: 'Thank-you page',
    images: [
      { key: 'thankYouBannerUrl', label: 'Thank-you page banner', aspect: '2 / 1' },
    ],
    fields: [
      { key: 'thankYouHeader', label: 'Title' },
      { key: 'thankYouCtaText', label: 'Action button' },
      { key: 'thankYouDescription', label: 'Body', textArea: true },
    ],
  },
  {
    title: 'Order status',
    images: [
      { key: 'orderStatusBackgroundUrl', label: 'Order status banner', aspect: '2 / 1' },
    ],
    fields: [
      { key: 'orderStatusHeader', label: 'Title' },
      { key: 'orderStatusCtaText', label: 'Action button' },
      { key: 'orderStatusDescription', label: 'Body', textArea: true },
    ],
  },
];

function shopifyPartnerFields() {
  var seen = {};
  var fields = [];
  SHOPIFY_PLACEMENTS.forEach(function (placement) {
    placement.fields.forEach(function (field) {
      if (!seen[field.key]) {
        seen[field.key] = true;
        fields.push(field);
      }
    });
  });
  return fields;
}

function urlOrEmpty(value) {
  if (value == null) {
    return '';
  }
  return String(value).trim();
}

function firstUrl(values) {
  for (var i = 0; i < values.length; i++) {
    var url = urlOrEmpty(values[i]);
    if (url) {
      return url;
    }
  }
  return '';
}

function shopifyBag(details) {
  var partnerData = details && details.partnerData;
  if (typeof partnerData === 'string') {
    try {
      partnerData = JSON.parse(partnerData);
    } catch (err) {
      partnerData = {};
    }
  }
  var shopify = partnerData && partnerData.shopify;
  if (typeof shopify === 'string') {
    try {
      shopify = JSON.parse(shopify);
    } catch (err) {
      shopify = {};
    }
  }
  return shopify && typeof shopify === 'object' && !Array.isArray(shopify) ? shopify : {};
}

function campaignImageBag(buildOptions) {
  var options = buildOptions;
  if (typeof options === 'string') {
    try {
      options = JSON.parse(options);
    } catch (err) {
      options = {};
    }
  }
  var images = options && options.images;
  return images && typeof images === 'object' && !Array.isArray(images) ? images : {};
}

function shopifyImageValue(details, key, campaignImages) {
  var shopify = shopifyBag(details);
  var images = campaignImages || {};

  // A cleared field on this edit stays empty. A missing or null key falls
  // through to the image the campaign already has.
  if (Object.prototype.hasOwnProperty.call(shopify, key) && shopify[key] === '') {
    return '';
  }
  var ownUrl = urlOrEmpty(shopify[key]);
  if (ownUrl) {
    return ownUrl;
  }

  if (key === 'floatingWidgetImageUrl') {
    // The widget banner is bannerImgSrc. When that was never set, the
    // lightbox still shows the campaign banner (desktop hero).
    return firstUrl([
      shopify.bannerImgSrc,
      details && details.shopifyImgUrl,
      images.floatingWidgetImageUrl,
      images.lightboxHeroUrl,
      images.desktopHeroUrl,
    ]);
  }
  if (key === 'staticPageHeroUrl') {
    return firstUrl([images.desktopHeroUrl, images.staticPageHeroUrl]);
  }
  if (key === 'thankYouBannerUrl') {
    return firstUrl([images.mobileHeroUrl, images.thankYouBannerUrl]);
  }
  if (key === 'orderStatusBackgroundUrl') {
    // Dedicated image first. The card otherwise uses the floating-widget
    // banner, then the same default background the extension shows.
    return firstUrl([
      images.orderStatusBackgroundUrl,
      shopify.bannerImgSrc,
      details && details.shopifyImgUrl,
      images.floatingWidgetImageUrl,
      DEFAULT_ORDER_STATUS_BANNER,
    ]);
  }
  return firstUrl([images[key]]);
}

function isShopifyCampaignVersion(details) {
  if (!details) {
    return false;
  }
  var tags = details.sourceTags;
  if (Array.isArray(tags) && tags.indexOf(SHOPIFY_SOURCE_TAG) !== -1) {
    return true;
  }
  var shopify = details.partnerData && details.partnerData.shopify;
  return !!(shopify && typeof shopify === 'object' && !Array.isArray(shopify));
}

function discountsTextFromVersion(details) {
  var shopify = details && details.partnerData && details.partnerData.shopify;
  var discounts = shopify && shopify.discounts;
  if (!discounts || typeof discounts !== 'object') {
    return '';
  }
  return JSON.stringify(discounts, null, 2);
}

let initialState = null;

@connect((store) => {
    return {
        campaignVersionGet: store.adminCampaignVersion.campaignVersionGet,
        campaignVersionEdit: store.adminCampaignVersion.campaignVersionEdit,
    };
})
class CampaignVersionEdit extends React.Component {
  constructor(props) {
    super(props);

    this.flowTypeOptions = [
      { label: 'None', value: null },
      {
        label: 'Sharer Triggered Reward',
        value: 'sharer_triggered_reward',
      },
      {
        label: 'Friend Triggered Reward',
        value: 'friend_triggered_reward',
      },
      { label: 'Both Triggered Reward', value: 'both_triggered_reward' },
      { label: 'Advertisement', value: 'advertisement' },
      { label: 'Direct Reward', value: 'direct_reward' },
      { label: 'Custom Flow', value: 'custom_flow' },
      { label: 'Marketplace Simple', value: 'mp_simple' },
      { label: 'Marketplace Promotion', value: 'mp_promotion' },
    ];

    const {
      campaignVersionDetails = {
        clientId: '',
        campaignId: '',
        name: '',
        alias: '',
        active: '',
        exposure: '',
        rewardPoolId: '',
        linkExpiryDays: '',
        privateLinkExpiryDays: '',
        documentUrl: '',
        sourceTags: [],
        archived: false,
        publicSharedUrlExpiresAt: '',
        privateSharedUrlExpiresAt: '',
        flowType: null,
        shopifyImgUrl: '',
        dialogConfirmationVisible: false,
      },
    } = props;

    this.state = {
      campaignVersionDetails,
      errors: {},
      displayErrorMessage: false,
      formSuccessfullySubmitted: false,
      flowType: null,
      shopifyDiscountsText: '',
      showShopifyPartnerData: false,
      shopifyImageUploading: null,
      shopifySourceImages: {},
      shopifyImageErrors: {},
    };

    this.onRewardPoolChange = this.onRewardPoolChange.bind(this);
  }

  onChange(name, { target }) {
    const { campaignVersionDetails } = this.state;
    _.set(campaignVersionDetails, name, target.value);
    this.setState({ campaignVersionDetails });
  }

  onActiveChange(name, { target }) {
    const { campaignVersionDetails } = this.state;
    if (target.value === 'true') {
      _.set(campaignVersionDetails, 'archived', false);
    }
    _.set(campaignVersionDetails, name, target.value);
    this.setState({ campaignVersionDetails });
  }

  onChangeCheckbox(name, { target }) {
    const { campaignVersionDetails } = this.state;
    _.set(campaignVersionDetails, name, target.checked);
    this.setState({ campaignVersionDetails });
  }

  onShopifyFieldChange(field, { target }) {
    const { campaignVersionDetails } = this.state;
    const partnerData = Object.assign({}, campaignVersionDetails.partnerData || {});
    const shopify = Object.assign({}, partnerData.shopify || {});
    shopify[field] = target.value;
    if (field === 'floatingWidgetImageUrl') {
      shopify.bannerImgSrc = target.value || null;
    }
    partnerData.shopify = shopify;
    campaignVersionDetails.partnerData = partnerData;
    this.setState({ campaignVersionDetails });
  }

  onShopifyImagePicked(field, event) {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (allowed.indexOf(file.type) === -1) {
      this.props.dispatch(globalMessageActions.showMessage({
        type: 'error',
        message: 'Image must be a JPEG, PNG, WEBP or GIF.',
        timeout: 10000,
      }));
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      this.props.dispatch(globalMessageActions.showMessage({
        type: 'error',
        message: 'Image must be 8MB or smaller.',
        timeout: 10000,
      }));
      return;
    }

    const campaignVersionId = this.state.campaignVersionDetails._id;
    const reader = new FileReader();
    reader.onload = () => {
      this.setState({ shopifyImageUploading: field });
      campaignVersionService.uploadShopifyImage(campaignVersionId, {
        field: field,
        dataUrl: reader.result,
      }).then((res) => {
        const url = res.data && res.data.url;
        this.onShopifyFieldChange(field, { target: { value: url || '' } });
        this.setState({ shopifyImageUploading: null });
      }).catch((err) => {
        const message = (err.response && err.response.data && err.response.data.message) || 'Image upload failed.';
        this.setState({ shopifyImageUploading: null });
        this.props.dispatch(globalMessageActions.showMessage({
          type: 'error',
          message: message,
          timeout: 10000,
        }));
      });
    };
    reader.readAsDataURL(file);
  }

  clearShopifyImage(field) {
    this.onShopifyFieldChange(field, { target: { value: '' } });
  }

  applyShopifyPartnerData(campaignVersionDetails) {
    const partnerData = Object.assign({}, campaignVersionDetails.partnerData || {});
    const shopify = Object.assign({}, partnerData.shopify || {});
    const discountsText = (this.state.shopifyDiscountsText || '').trim();
    const alreadyShopify = isShopifyCampaignVersion(campaignVersionDetails);
    const hasEnteredValue = !!discountsText || shopifyPartnerFields().some(function (field) {
      var value = shopify[field.key];
      return value !== undefined && value !== null && String(value).trim() !== '';
    });

    if (!alreadyShopify && !hasEnteredValue) {
      return null;
    }

    shopifyPartnerFields().forEach(function (field) {
      var value = shopify[field.key];
      shopify[field.key] = value === '' || value === undefined ? null : value;
    });

    if (!discountsText) {
      shopify.discounts = null;
    } else {
      try {
        var parsed = JSON.parse(discountsText);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          return 'Shopify discounts must be a JSON object';
        }
        shopify.discounts = parsed;
      } catch (err) {
        return 'Shopify discounts must be valid JSON';
      }
    }

    var floatingImage = shopify.floatingWidgetImageUrl || shopify.bannerImgSrc || null;
    if (floatingImage) {
      shopify.floatingWidgetImageUrl = floatingImage;
      shopify.bannerImgSrc = floatingImage;
    }

    partnerData.shopify = shopify;
    campaignVersionDetails.partnerData = partnerData;
    return null;
  }

  componentWillMount() {
    var campaignVersionId = this.props.params.campaignVersionId;
    console.log(campaignVersionId);
    if (campaignVersionId) {
      this.props.dispatch(
        campaignVersionActions.getCampaignVersion(campaignVersionId)
      );

      if (
        this.props.location &&
                this.props.location.state &&
                this.props.location.state.fromCreatePage
      ) {
        this.props.router.replace({
          ...this.props.location,
          state: null,
        });
        this.props.dispatch(
          globalMessageActions.showMessage({
            type: 'success',
            message: 'New campaign version added',
            timeout: 20000,
          })
        );
      }
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.campaignVersionGet) {
      //nextProps.campaign.expiry = new Date(nextProps.campaign.expiry);
      campaignService
        .getCampaign(nextProps.campaignVersionGet.campaignId)
        .then((data) => {
          if (data.data) {
            const { campaignVersionDetails } = this.state;
            initialState = _.cloneDeep(campaignVersionDetails);
            initialState.activeCampaign = data.data.active;
            _.set(
              campaignVersionDetails,
              'clientId',
              data.data.clientId
            );
            this.setState({
              campaignVersionDetails,
              shopifySourceImages: campaignImageBag(data.data.buildOptions),
            });
          } else {
            nextProps.campaignVersionGet.clientId = null;
          }
        });

      this.setState({
        campaignVersionDetails: nextProps.campaignVersionGet,
        hardStop:
                    nextProps.campaignVersionGet.linkExpiryDays == -1
                      ? true
                      : false,
      });
      this.setState({
        campaignVersionDetails: nextProps.campaignVersionGet,
        hardStop:
                    nextProps.campaignVersionGet.privateLinkExpiryDays == -1
                      ? true
                      : false,
      });

      var nextId = nextProps.campaignVersionGet._id;
      var prevId = this.props.campaignVersionGet && this.props.campaignVersionGet._id;
      if (nextId && nextId !== prevId) {
        this.setState({
          shopifyDiscountsText: discountsTextFromVersion(nextProps.campaignVersionGet),
        });
      }
    }
  }

  componentWillUpdate(nextProps) {
    if (this.props.campaignVersionEdit != nextProps.campaignVersionEdit) {
      if (_.get(nextProps, 'campaignVersionEdit.name')) {
        this.props.dispatch(
          globalMessageActions.showMessage({
            type: 'success',
            message: 'Campaign version saved with success',
            timeout: 20000,
          })
        );
      }
      if (_.get(nextProps, 'campaignVersionEdit.data')) {
        this.props.dispatch(
          globalMessageActions.showMessage({
            type: 'error',
            message:
                            'Error: ' +
                            nextProps.campaignVersionEdit.data.message,
            timeout: 10000,
          })
        );
      }
    }
  }

  onPublicSharedUrlExpiresAtChange(date) {
    const { campaignVersionDetails } = this.state;
    _.set(campaignVersionDetails, 'publicSharedUrlExpiresAt', date.value);
    this.setState({ campaignVersionDetails });
  }

  onPrivateSharedUrlExpiresAtChange(date) {
    const { campaignVersionDetails } = this.state;
    _.set(campaignVersionDetails, 'privateSharedUrlExpiresAt', date.value);
    this.setState({ campaignVersionDetails });
  }

  onSubmit() {
    this.setState({ displayErrorMessage: false });
    const { campaignVersionDetails, errors } = this.state;
    let checkErrors = false;

    this.validateForm();

    const shopifyError = this.applyShopifyPartnerData(campaignVersionDetails);
    errors.shopifyDiscounts = shopifyError || '';
    if (shopifyError) {
      this.setState({ errors: errors, displayErrorMessage: true, showShopifyPartnerData: true });
      this.props.dispatch(
        globalMessageActions.showMessage({
          type: 'error',
          message: shopifyError,
          timeout: 10000,
        })
      );
      return;
    }

    for (const key in errors) {
      if (errors[key]) {
        checkErrors = true;
        this.setState({ displayErrorMessage: true });
        this.props.dispatch(
          globalMessageActions.showMessage({
            type: 'error',
            message: 'Please fill all the required',
            timeout: 10000,
          })
        );
        break;
      }
    }

    if (!checkErrors) {
      campaignVersionDetails.updatedAt = new Date();
      let campaignVersionDetailsWithNulls = _.mapValues(
        campaignVersionDetails,
        function(v) {
          // explicitly set empty vals to null
          if (v === '') {
            return null;
          } else {
            return v;
          }
        }
      );

      if (
        !initialState.approved &&
                initialState.activeCampaign &&
                (initialState.name != campaignVersionDetailsWithNulls.name ||
                    initialState.alias !=
                        campaignVersionDetailsWithNulls.alias ||
                  (   
                    (
                      initialState.sourceTags.length !=
                            campaignVersionDetailsWithNulls.sourceTags.length ||
                        !initialState.sourceTags.every((st) =>
                        campaignVersionDetailsWithNulls.sourceTags.includes(st)
                      ))
                  )
                )
      ) {
        this.setState({ dialogConfirmationVisible: true });
      } else {
        this.props.dispatch(
          campaignVersionActions.updateCampaignVersion(
            campaignVersionDetailsWithNulls
          )
        );
      }
    }
  }

  validateForm() {
    const { campaignVersionDetails, errors } = this.state;

    errors['campaignId'] = validator.isEmpty(
      campaignVersionDetails.campaignId
    );
    errors['name'] = validator.isEmpty(campaignVersionDetails.name);
    errors['exposure'] = validator.isEmpty(
      campaignVersionDetails.exposure.toString()
    );
    errors['active'] = validator.isEmpty(
      campaignVersionDetails.active.toString()
    );
    errors['alias'] = validator.isEmpty(campaignVersionDetails.alias);
    errors['linkExpiryDays'] = (() => {
      if (campaignVersionDetails.linkExpiryDays === '') {
        return true;
      }
      return !validator.isInt(
        campaignVersionDetails.linkExpiryDays + '',
        { min: -1, max: 999 }
      );
    })();

    errors['privateLinkExpiryDays'] = (() => {
      if (campaignVersionDetails.privateLinkExpiryDays === '') {
        return true;
      }
      return !validator.isInt(
        campaignVersionDetails.privateLinkExpiryDays + '',
        { min: -1, max: 999 }
      );
    })();

    return this.setState({ errors });
  }

  onRewardPoolChange(e) {
    this.setState({ rewardPoolId: e.value });
    const { campaignVersionDetails } = this.state;
    _.set(campaignVersionDetails, 'rewardPoolId', e.value);
    this.setState({ campaignVersionDetails });
  }

  dialogConfirmationFooter = () => {
    return (
      <div
        style={{
          display: 'flex',
          placeContent: 'center',
          width: '100%',
          gap: '20px',
        }}
        className="ui-dialog-buttonpanel p-clearfix"
      >
        <div
          style={{
            display: 'flex',
            placeContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Button
            label="Confirm"
            onClick={(e) => {
              initialState.approved = true;
              this.onSubmit();

              this.setState({ dialogConfirmationVisible: false });
            }}
          />
        </div>
        <div
          style={{
            display: 'flex',
            placeContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Button
            label="Cancel"
            onClick={() =>
              this.setState({ dialogConfirmationVisible: false })
            }
          />
        </div>
      </div>
    );
  };

  renderConfirmationDialog = () => {
    var headerName = `Confirmation`;

    return (
      <Dialog
        header={headerName}
        footer={this.dialogConfirmationFooter()}
        visible={this.state.dialogConfirmationVisible}
        width="550px"
        modal={true}
        resizable={true}
        responsive={true}
        onHide={() => {
          this.setState({ dialogConfirmationVisible: false });
        }}
      >
        <div className="content-section implementation">
          <span>
                      You are editing <b>*name *alias or *source tags</b> from
                      an Campaign Version of an active Campaign. Be aware it
                      may cause problems of statistics eg: order duplication
                      specially if the campaign was launched some time ago. If
                      you are not sure about it, please ask for some
                      assistance.
          </span>
        </div>
      </Dialog>
    );
  };

  onShopifyImageError(field, src) {
    this.setState(function (state) {
      var errors = Object.assign({}, state.shopifyImageErrors);
      errors[field] = src;
      return { shopifyImageErrors: errors };
    });
  }

  renderShopifyImageField(field, label, value, aspect) {
    const uploading = this.state.shopifyImageUploading === field;
    const inputId = 'shopify-image-file-' + field;
    const failed = value && this.state.shopifyImageErrors[field] === value;
    return (
      <div className={'shopify-image-picker' + (value ? ' has-image' : ' is-empty')} key={field}>
        <div className="shopify-image-picker__label">{label}</div>
        <div
          className="shopify-image-picker__frame"
          style={{ aspectRatio: aspect || '2 / 1' }}
          onClick={() => {
            if (!this.state.shopifyImageUploading) {
              document.getElementById(inputId).click();
            }
          }}
        >
          {value && !failed ? (
            <img
              src={value}
              alt={label}
              onError={() => this.onShopifyImageError(field, value)}
            />
          ) : (
            <span className="shopify-image-picker__empty">
              {uploading ? 'Uploading…' : failed ? 'Image could not be loaded' : 'No image yet'}
            </span>
          )}
        </div>
        <input
          id={inputId}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          className="shopify-image-picker__file"
          onChange={(event) => this.onShopifyImagePicked(field, event)}
        />
        <div className="shopify-image-picker__actions">
          <button
            type="button"
            onClick={() => document.getElementById(inputId).click()}
            disabled={!!this.state.shopifyImageUploading}
          >
            {uploading ? 'Uploading…' : value ? 'Replace' : 'Upload'}
          </button>
          {value ? (
            <a href={value} target="_blank" rel="noreferrer">Open</a>
          ) : null}
          {value ? (
            <button
              type="button"
              className="shopify-image-picker__remove"
              onClick={() => this.clearShopifyImage(field)}
              disabled={uploading}
            >
              Remove
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  renderShopifyPartnerData(campaignVersionDetails, errors) {
    const shopify =
      (campaignVersionDetails.partnerData && campaignVersionDetails.partnerData.shopify) || {};

    return (
      <Panel
        header="Shopify"
        toggleable={true}
        className={'shopify-partner-data' + (this.state.showShopifyPartnerData ? ' is-open' : ' is-collapsed')}
        collapsed={!this.state.showShopifyPartnerData}
        onCollapse={() => this.setState({ showShopifyPartnerData: !this.state.showShopifyPartnerData })}
      >
        <p>
          Stored on this campaign version as partner data. Save writes it
          through the admin API and does not run campaign setup.
        </p>
        {SHOPIFY_PLACEMENTS.map((placement) => (
          <Card
            key={placement.title}
            title={placement.title}
            subtitle={placement.hint || null}
            className="ui-card-shadow shopify-placement-card"
          >
            {placement.images.length ? (
              <div className="shopify-image-grid">
                {placement.images.map((field) =>
                  this.renderShopifyImageField(
                    field.key,
                    field.label,
                    shopifyImageValue(
                      campaignVersionDetails,
                      field.key,
                      this.state.shopifySourceImages
                    ),
                    field.aspect
                  )
                )}
              </div>
            ) : null}
            {placement.fields.map((field) => (
              <FormElement
                key={placement.title + '-' + field.key}
                label={field.label}
                name={field.key}
                type={field.color ? 'color' : field.textArea ? 'textArea' : 'text'}
                placeholder={field.placeholder}
                onChange={this.onShopifyFieldChange.bind(this, field.key)}
                value={shopify[field.key] || ''}
                id={'form-shopify-' + placement.title.replace(/\s+/g, '-').toLowerCase() + '-' + field.key}
              />
            ))}
          </Card>
        ))}
        <Card title="Discounts" className="ui-card-shadow shopify-placement-card">
          <FormElement
            label="Discounts (JSON)"
            name="shopifyDiscounts"
            type="textArea"
            rows={8}
            placeholder='{"sharer":{"mode":"evergreen","code":"SHARE10","shopifyDiscountGid":""},"friend":{"mode":"batch","codePrefix":"FRIEND","poolSize":50,"shopifyDiscountGid":""}}'
            onChange={(e) => this.setState({ shopifyDiscountsText: e.target.value })}
            value={this.state.shopifyDiscountsText}
            error={errors.shopifyDiscounts}
            id="form-shopify-discounts"
          />
        </Card>
      </Panel>
    );
  }

  render() {
    const {
      campaignVersionDetails = { info: {} },
      sourceTag,
      errors,
    } = this.state;
    const { hardStop } = this.state;

    const {
      clientId,
      campaignId,
      name,
      alias,
      sourceTags,
      active,
      exposure,
      rewardPoolId,
      linkExpiryDays,
      privateLinkExpiryDays,
      mpOfferTitle,
      documentUrl,
      trackingLink,
      affTrackingLinkOnLoad,
      shopifyImgUrl,
      archived,
    } = campaignVersionDetails;

    return (
      <div>
        <h3>Edit campaign version</h3>
        <form className="c-form--flex">
          <FormElement
            label="Campaign Id"
            name="campaignId"
            type="text"
            onChange={this.onChange.bind(this, 'campaignId')}
            value={campaignId}
            error={errors.campaignId}
            id="form-campaignId"
            required
            disabled
          />

          <FormElement
            label="Name"
            name="name"
            type="text"
            onChange={this.onChange.bind(this, 'name')}
            value={name}
            error={errors.name}
            id="form-name"
            required
          />

          <FormElement
            label="Exposure"
            name="exposure"
            type="text"
            onChange={this.onChange.bind(this, 'exposure')}
            value={exposure}
            error={errors.exposure}
            id="form-exposure"
            required
          />

          <RewardPoolDropbox
            label="Reward Pool"
            client={clientId}
            value={rewardPoolId}
            onChange={this.onRewardPoolChange}
            error={errors.rewardPoolId}
            id="form-rewardpoolid"
          />

          <fieldset>
            <legend>Expiration</legend>
            <div className="ui-g">
              <div className="ui-g-12 ui-md-6 ui-lg-3">
                <FormElement
                  label="Public Links Expiration Absolute Date (This field uses UTC time)"
                  name="publicShareUrlExpiresAt"
                  tooltip="A Disabled Button"
                  type="calendarWithTime"
                  onChange={(e) =>
                    this.onPublicSharedUrlExpiresAtChange(e)
                  }
                  value={
                    this.state.campaignVersionDetails
                      .publicSharedUrlExpiresAt
                      ? new Date(
                        this.state.campaignVersionDetails.publicSharedUrlExpiresAt
                      )
                      : ''
                  }
                  id="start-date"
                  style={{
                    marginLeft: '5px',
                    width: '200px',
                  }}
                />
              </div>

              <div className="ui-g-12 ui-md-6 ui-lg-3">
                <FormElement
                  label="Private Links Expiration Absolute Date (This field uses UTC time)"
                  name="privateShareUrlExpiresAt"
                  type="calendarWithTime"
                  onChange={(e) =>
                    this.onPrivateSharedUrlExpiresAtChange(
                      e
                    )
                  }
                  value={
                    this.state.campaignVersionDetails
                      .privateSharedUrlExpiresAt
                      ? new Date(
                        this.state.campaignVersionDetails.privateSharedUrlExpiresAt
                      )
                      : ''
                  }
                  id="expiry"
                  style={{
                    marginLeft: '5px',
                    width: '200px',
                  }}
                />
              </div>
              <div className="ui-g-12 ui-md-6 ui-lg-3">
                <div className="daysExpiry">
                  <FormElement
                    label="days until public link expiry"
                    name="linkExpiryDays"
                    type="text"
                    onChange={this.onChange.bind(
                      this,
                      'linkExpiryDays'
                    )}
                    disabled={hardStop}
                    value={linkExpiryDays}
                    error={errors.linkExpiryDays}
                    id="form-link-expiry-days"
                    required
                  />
                </div>
              </div>

              <div className="ui-g-12 ui-md-6 ui-lg-3">
                <div className="daysExpiry">
                  <FormElement
                    label="days until private link expiry"
                    name="priviteLinkExpiryDays"
                    type="text"
                    onChange={this.onChange.bind(
                      this,
                      'privateLinkExpiryDays'
                    )}
                    disabled={hardStop}
                    value={privateLinkExpiryDays}
                    error={errors.priviteLinkExpiryDays}
                    id="form-private-link-expiry-days"
                    required
                  />
                  <div class="tooltip">
                    <i
                      class="fa fa-question-circle"
                      aria-hidden="true"
                    ></i>
                    <span class="tooltiptext">
                                            The Shared Url will expire according
                                            to the <b> Absolute Date</b> or{' '}
                      <b> Days until link expiry</b>. The
                                            closest expiration date will be
                                            considered. The absolute date can be
                                            null, so only{' '}
                      <b> Days until link expiry</b> will
                                            be considered.
                      <b> Days until link expiry</b> is
                                            calculated from the Shared Url
                                            creation’s date.
                      <b>Public links</b> are the ones
                                            generated during the sharing
                                            process. <b>Private links</b> are
                                            the ones generated for a single
                                            targeted person.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>

          <FormElement
            label="Marketplace Offer Title"
            name="mpOfferTitle"
            type="text"
            onChange={this.onChange.bind(this, 'mpOfferTitle')}
            value={mpOfferTitle}
            id="form-marketplace-offer-title"
          />

          <FormElement
            label="Document URL"
            name="documentUrl"
            type="text"
            onChange={this.onChange.bind(this, 'documentUrl')}
            disabled={hardStop}
            value={documentUrl}
            id="form-document-url"
          />

          <FormElement
            label="Tracking Link"
            name="trackingLink"
            type="text"
            onChange={this.onChange.bind(this, 'trackingLink')}
            value={trackingLink}
            id="tracking-link"
          />

          <div className="affTrackingLinkOnLoad">
            <FormElement
              label="Affiliate Tracking Link on Landing Page Load"
              name="affTrackingLinkOnLoad"
              type="text"
              onChange={this.onChange.bind(
                this,
                'affTrackingLinkOnLoad'
              )}
              value={affTrackingLinkOnLoad}
              id="affTrackingLinkOnLoad"
            />
            <div class="tooltip">
              <i
                class="fa fa-question-circle"
                aria-hidden="true"
              ></i>
              <span class="tooltiptext">
                                This tracking link is used by the landing page
                                on its automatic redirection process on load.
                                The user has no access to this link. This value
                                must implement 2 dynamic values,{' '}
                <b>{'{sua_id}'}</b> to the click identification
                                and <b>{'{red_url}'}</b> to the callback url
              </span>
            </div>
          </div>

          <div className="affTrackingLinkOnLoad">
            <FormElement
              label="Shopify image URL"
              name="shopifyImgUrl"
              type="text"
              onChange={this.onChange.bind(this, 'shopifyImgUrl')}
              value={shopifyImgUrl}
              id="form-shopify-image-url"
            />
            <div class="tooltip">
              <i class="fa fa-question-circle" aria-hidden="true"></i>
              <span class="tooltiptext">
                Image for the original thank-you banner. Clients on Shopify
                version 1 use this URL.
              </span>
            </div>
          </div>

          <FormElement
            label="Hard Stop"
            name="hardStop"
            type="checkbox"
            onChange={(e) => {
              _.set(
                campaignVersionDetails,
                'linkExpiryDays',
                e.target.checked ? -1 : 30
              );
              _.set(
                campaignVersionDetails,
                'privateLinkExpiryDays',
                e.target.checked ? -1 : 30
              );
              this.setState({
                hardStop: e.target.checked,
                campaignVersionDetails,
              });
            }}
            checked={!!hardStop}
            error={errors.hardStop}
            id="form-active"
          />

          <FormElement
            label="Source Tag"
            name="sourceTag"
            type="text"
            onChange={(e) => {
              if (e.target.fromSugestion) {
                common.addTagOption(this, e.target.value);
              } else {
                this.setState({ sourceTag: e.target.value });
              }
            }}
            onKeyPress={(e) => {
              if (e.key == 'Enter') {
                common.addTagOption(this, sourceTag);
              }
            }}
            value={sourceTag}
            error={errors.sourceTag}
            id="form-source-tag"
            fillingOptions={[
              'CONFIRMATION_PAGE',
              'STATIC_PAGE_ON_SORETO',
              'STATIC_PAGE_ON_CLIENT',
              SHOPIFY_SOURCE_TAG,
            ]}
          ></FormElement>

          <div>{common.sourceTagOptions(this, sourceTags)}</div>

          <FormElement
            label="Flow Type"
            name="flowType"
            type="dropdown"
            onChange={(e) => {
              const { campaignVersionDetails } = this.state;
              _.set(campaignVersionDetails, 'flowType', e.value);
              this.setState({ campaignVersionDetails });
            }}
            value={this.state.campaignVersionDetails.flowType}
            style={{ marginLeft: '5px', width: '300px' }}
            error={errors.flowType}
            options={this.flowTypeOptions}
            id="type"
          />

          <FormElement
            label="Alias"
            name="alias"
            type="text"
            onChange={this.onChange.bind(this, 'alias')}
            value={alias}
            error={errors.alias}
            id="form-alias"
            required
          />

          <FormElement
            label="Active"
            name="active"
            type="select"
            onChange={this.onActiveChange.bind(this, 'active')}
            value={active}
            error={errors.active}
            id="form-active"
            required
          >
            <option value=""> </option>
            <option value="true">true</option>
            <option value="false">false</option>
          </FormElement>

          <FormElement
            label="Archived"
            name="archived"
            type="checkbox"
            onChange={this.onChangeCheckbox.bind(this, 'archived')}
            disabled={active == 'true' || active === true}
            checked={!!archived}
            error={errors.archived}
            id="form-active"
          />

          {this.renderShopifyPartnerData(campaignVersionDetails, errors)}
        </form>
        <br />
        <button onClick={() => this.onSubmit()}>Save</button>

        <button
          className="button icon-left"
          onClick={() =>
            browserHistory.push('/admin/campaignVersion')
          }
        >
                    Back
        </button>

        {this.renderConfirmationDialog()}
      </div>
    );
  }
}

export default CampaignVersionEdit;
